<template>
    <div class="director-interest-create-or-edit">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isDirectorInterestSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-director-interest-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                        <validation-provider
                            v-slot="{ errors }"
                            name="director"
                            rules="required"
                        >
                            <b-form-group label="Director" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="director"
                                    ref="director"
                                    v-model="directorInterestLocal.director"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select director"
                                    label="name"
                                    :disabled = "isDisabled || isDirectorEdit"
                                    :state="errors.length > 0 ? false : null"
                                    :options="directorOptions"
                                    @input="handleDirectorOnChange"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                                v-slot="{ errors }"
                                name="date"
                                rules="required"
                            >
                                <b-form-group label="Date" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="directorInterestLocal.date"
                                        placeholder="Enter date"
                                        class="form-control"
                                        data-test="date"
                                        :config="datePickerConfig"
                                        :disabled = "isDisabled"
                                        :style="dynamicStyle"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="type of shares"
                            rules="required"
                        >
                            <b-form-group label="Type of shares" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="type-of-shares"
                                    ref="shareType"
                                    v-model="directorInterestLocal.typeOfShares"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select type of shares"
                                    label="name" 
                                    :state="errors.length > 0 ? false : null"
                                    :disabled = "isDisabled"
                                    :options="shareTypeOptions"
                                    @input="handleTypeOfSharesOnChange"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="validationContext"
                            name="number of shares"
                            rules="required"
                        >
                            <b-form-group label="Number of shares" label-for="number-of-shares">
                                <b-form-input
                                    id="number-of-shares"
                                    v-model="directorInterestLocal.numberOfShares"
                                    v-mask="shareMask"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    :disabled = "isDisabled"
                                    trim
                                    placeholder="Enter number of shares"
                                    data-test ="number-of-shares"
                                    type="text"
                                    no-wheel
                                    @keydown.up.prevent
                                    @keydown.down.prevent
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="currencies"
                            rules="required"
                        >
                            <b-form-group label="Currencies" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="currencies"
                                    ref="currencies"
                                    v-model="directorInterestLocal.currency"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select currency"
                                    label="name" 
                                    :state="errors.length > 0 ? false : null"
                                    :disabled = "isDisabled"
                                    :options="currencyOptions"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isCurrencyOptionsLoading" />
                                        <span v-else> {{ currencyEmptyMessage }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="type"
                            rules="required"
                        >
                            <b-form-group label="Type" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="type"
                                    ref="type"
                                    v-model="directorInterestLocal.type"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select type"
                                    label="name" 
                                    :state="errors.length > 0 ? false : null"
                                    :disabled = "isDisabled"
                                    :options="typeOptions"
                                    @input="handleTypeOnChange"
                                >
                                    <template #option="typeOptions">
                                        {{ typeOptions.name }} 

                                         <!-- <b-tooltip v-if="selectedTip" target="relatedCorporation" placement="right" >
                                             {{ selectedTip.value }}
                                        </b-tooltip> -->

                                        <!-- <span 
                                            v-if = "typeOptions.name === 'NAME OF RELATED CORPORATION'"
                                            class="select-toll-tips" 
                                            style="width:12px; left:3px;"
                                            @mouseover="showToastTip(typeOptions.name)"
                                        > 
                                            <vector
                                                id="relatedCorporation"
                                                data-type="icon"
                                                data-width="13px"
                                                data-height="13px"
                                                style="position:relative; bottom:3px;"
                                                    data-name="information"
                                            >
                                            </vector>
                                        </span> -->
                                    </template>

                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ typeEmptyMessage }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="how held"
                            rules="required"
                        >
                            <b-form-group label="How held" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="how-held"
                                    ref="howHeld"
                                    v-model="directorInterestLocal.howHeld"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select how held"
                                    label="name" 
                                    :state="errors.length > 0 ? false : null"
                                    :disabled = "isDisabled"
                                    :options="howHeldOptions"
                                    @input="handleHowHeldOnChange"
                                >
                                    <template #option="howHeldOptions">
                                        {{ howHeldOptions.name }}
                                        <span 
                                            v-if = "howHeldOptions.name !== 'In own name'"
                                            class="select-toll-tips" 
                                            style="width:12px; left:3px;"
                                            @mouseover="showToastTip(howHeldOptions.name)"
                                        > 
                                            <b-tooltip v-if="selectedTip" :target="getDynamicId(howHeldOptions.name)" placement="right">
                                                 {{ selectedTip.value }}
                                            </b-tooltip>

                                            <b-tooltip v-if="selectedTip" :target="getDynamicId(howHeldOptions.name)" placement="right">
                                                {{ selectedTip.value }} 
                                            </b-tooltip>
                                            
                                            <vector
                                                :id="getDynamicId(howHeldOptions.name)"
                                                data-type="icon"
                                                data-width="13px"
                                                data-height="13px"
                                                style="position:relative; bottom:3px;"
                                                data-name="information"
                                        >
                                        </vector>
                                        </span>
                                    </template>

                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ howHeldEmptyMessage }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="validationContext"
                            :name="getDynamicCompanyLabel.toLowerCase()"
                            rules="required"
                        >
                            <b-form-group 
                                :label="getDynamicCompanyLabel" 
                                label-for="name-of-company"
                            >
                                <b-form-input
                                    id="name-of-company"
                                    v-model="directorInterestLocal.companyName"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    :placeholder="`Enter ${getDynamicCompanyLabel.toLowerCase()}`"
                                    :disabled="shouldDisabledCompany"
                                    data-test ="name-of-company"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="validationContext"
                            :name="getDynamicNameLabel.toLowerCase()"
                            rules="required"
                        >
                            <b-form-group 
                                :label="getDynamicNameLabel" 
                                label-for="holder-name"
                            >
                                <b-form-input
                                    id="holder-name"
                                    v-model="directorInterestLocal.holderName"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    :placeholder="`Enter ${getDynamicNameLabel.toLowerCase()}`"
                                    :disabled="shouldDisabledName"
                                    data-test ="holder-name"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <div class="mt-3 mb-1">INTEREST OF RIGHT ACQUIRED</div>
                        <validation-provider
                            v-slot="{ errors }"
                            name="date of agreement"
                            :rules="(shouldDisableCessation || (! shouldDisableAcquire && ! shouldDisableCessation)) ? 'required' : ''"
                        >
                            <b-form-group label="Date of agreement" :state="errors.length > 0 ? false : null">
                                <flat-pickr
                                    v-model="directorInterestLocal.dateOfAgreement"
                                    placeholder="Enter date of agreement"
                                    class="form-control"
                                    data-test="date-of-agreement"
                                    :config="datePickerConfig"
                                    :disabled = "isDisabled || shouldDisableAcquire"
                                    :style="dynamicStyle"
                                    :state="errors.length > 0 ? false : null"
                                    @input="onAcquireChange"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="price or other consideration"
                            :rules="(shouldDisableCessation || (! shouldDisableAcquire && ! shouldDisableCessation)) ? 'required' : ''"
                        >
                            <b-form-group label="Price or other consideration" label-for="price-or-other-consideration">
                                <b-input-group>
                                    <template #prepend>
                                        <v-select
                                            id="acquiredCurrency"
                                            ref="acquiredCurrency"
                                            v-model="directorInterestLocal.acquiredPriceCurrency"
                                            class="custom-select-icon"
                                            :clearable="false"
                                            :state="errors.length > 0 ? false : null"
                                            placeholder="" 
                                            label="currency_code"
                                            :disabled = "isDisabled || shouldDisableAcquire"
                                            :options="allCurrencies"
                                        >
                                        </v-select>
                                    </template>

                                    <b-form-input
                                        id="price-or-other-consideration"
                                        v-model="directorInterestLocal.priceOrOtherConsideration"
                                        v-mask="amountMask"
                                        autofocus
                                        :state="errors.length > 0 ? false : null"
                                        :disabled = "isDisabled || shouldDisableAcquire"
                                        trim
                                        placeholder="Enter price or other consideration"
                                        data-test ="price-or-other-consideration"
                                        type="text"
                                        no-wheel
                                        @keydown.up.prevent
                                        @keydown.down.prevent
                                        @input="onAcquireChange"
                                    />                             
                                </b-input-group>    
                                <small class="text-danger">{{ errors[0] }}</small>                           
                            </b-form-group>
                        </validation-provider>

                        <!-- Cessation field -->
                        <div class="mt-3 mb-1">CESSATION OF INTEREST OF RIGHT</div>
                            <validation-provider
                                    v-slot="{ errors }"
                                    name=" date of agreement"
                                    :rules="(shouldDisableAcquire || (! shouldDisableAcquire && ! shouldDisableCessation)) ? 'required' : ''"
                                >
                                    <b-form-group label="Date of agreement" :state="errors.length > 0 ? false : null">
                                        <flat-pickr
                                            v-model="directorInterestLocal.cessationDateOfAgreement"
                                            placeholder="Enter date of agreement"
                                            class="form-control"
                                            data-test="cessation-date-of-agreement"
                                            :config="datePickerConfig"
                                            :disabled = "isDisabled || shouldDisableCessation"
                                            :style="dynamicStyle"
                                            :state="errors.length > 0 ? false : null"
                                            @input="onCessationChange"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name=" price or other consideration"
                                :rules="(shouldDisableAcquire || (! shouldDisableAcquire && ! shouldDisableCessation)) ? 'required' : ''"
                            >
                                <b-form-group label="Price or other consideration" label-for="cessation-price-or-other-consideration">
                                    <b-input-group>
                                        <template #prepend>
                                            <v-select
                                                id="cessationCurrency"
                                                ref="cessationCurrency"
                                                v-model="directorInterestLocal.cessationPriceCurrency"
                                                class="custom-select-icon"
                                                :clearable="false"
                                                :state="errors.length > 0 ? false : null"
                                                placeholder="" 
                                                label="currency_code"
                                                :disabled = "isDisabled || shouldDisableCessation"
                                                :options="allCurrencies"
                                            >
                                            </v-select>
                                        </template>

                                        <b-form-input
                                            id="cessation-price-or-other-consideration"
                                            v-model="directorInterestLocal.cessationPriceOrOtherConsideration"
                                            v-mask="amountMask"
                                            autofocus
                                            :state="errors.length > 0 ? false : null"
                                            :disabled = "isDisabled || shouldDisableCessation"
                                            trim
                                            placeholder="Enter price or other consideration"
                                            data-test ="cessation-price-or-other-consideration"
                                            type="text"
                                            no-wheel
                                            @keydown.up.prevent
                                            @keydown.down.prevent
                                            @input="onCessationChange"
                                        />
                                    </b-input-group>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                        <!-- Form Actions -->
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                type="submit"
                                data-test="create-director-interest-btn"
                                >
                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    {{ dataStatus === 'edit' ? "Update" : "Add " }}
                                </span>

                                <img
                                    v-if="dataStatus === 'create' && !dataSidebarActionLoading"
                                    :src="'/illustrations/add-circle.svg'"
                                    class="ml-1"
                                    alt="add-logo"
                                />

                                <img
                                    v-if="dataStatus === 'edit' && !dataSidebarActionLoading"
                                    :src="'/illustrations/edit.svg'"
                                    class="ml-1"
                                    alt="edit-logo"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-director-interest-sidebar-active', false)"
                                >
                                Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BButton,
    BTooltip,
    BFormInvalidFeedback,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email, required_one_of } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { mapGetters } from 'vuex';
import DirectorInterest from '@/models/DirectorInterest';
import { DIRECTOR_INTEREST } from '@/constants/config';
import { NO_MATCHING_OPTIONS } from "@/constants/errors";
import Company from '@/models/Company';
import Currency from '@/models/Currency';
import { capitalize, shareMask, amountMask, unMask } from '@/helpers';

export default {
    components: {
        // BSV
        BForm,
        vSelect,
        BButton,
        BTooltip,
        
        BSidebar,
        flatPickr,
        BFormGroup,
        BFormInput,
        BInputGroup,
        BFormInvalidFeedback,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isDirectorInterestSidebarActive",
        event: "update:is-director-interest-sidebar-active",
    },

    props: {
        isDirectorInterestSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },

        dataDirectorInterest: {
            type: Object,
            required: true,
        },
        
        dataStatus: { 
            type: String,
            default: "view"
        },
    },

    data() {
        return {
            required,
            email,
            required_one_of,
            capitalize,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            shareTypeOptions: [],
            currencyOptions: [],
            allCurrencies: [],
            directorOptions: [],
            howHeldOptionsData : [],
            howHeldOptions: [],
            typeOptionsData: [],
            typeOptions: [],
            shouldDisableAcquire: false,
            shouldDisableCessation: false,
            isCurrencyOptionsLoading: false,

            isFetching: false,
            shouldShowTipBox: false,
            NO_MATCHING_OPTIONS,

            selectedTip: undefined,
            tips : [
                // {name: 'name of related corporation', value : 'See Section 6 of the Companies Act, which defines related corporation(s) to include the holding company of another corporation, a subsidiary of another corporation, or a subsidiary of the holding company of another corporation.'},
                // {name: 'interest of immediate family members', value : 'See Section 7 of the Companies Act and Sections 4 and 130(5) of the Securities and Futures Act. A director / chief executive officer is deemed interested in the shares and debentures in which his immediate family (i.e. spouse and children under 21 years, including step-children and adopted children, provided such person is not also a director or chief executive officer) has an interest. Relevant details of how the deemed interest arises should be provided.'},
                // {name: 'other deemed interest', value : 'Section 7 of the Companies Act defines other deemed interests (for example, interests under trust, and interests through associated persons or corporations). '}
                {name: DIRECTOR_INTEREST.HOW_HELD.HOW_HELD_SPOUSE_NOMINEE_OPTIONS, value : `A director's interest or a right in or over any shares or debentures are deemed to include shares or debentures held by the director's spouse (not herself or himself a director thereof) or infant children (including step and adopted children).   Securities registered in the names of (a) nominees in which a director has a beneficial interest should be disclosed and (b) registered in the names of directors, the beneficial interest of which is vested in another party, should be disclosed together with details of the beneficial holder(s).`},
                {name: DIRECTOR_INTEREST.HOW_HELD.HOW_HELD_NAME_OF_BODIES_OPTIONS, value : "This refers to companies or corporations in which a director has a controlling interest or together with his associates control more than 20% of the voting shares therein or if the companies or corporations or its directors are accustomed or under an obligation to act in accordance with the directors' directions, instructions or wishes."}
            ],

            shareMask,
            amountMask
        };
    },

    computed: {
        ...mapGetters({
            companyName: 'company/fullCompanyName'
        }),

        getDynamicCompanyLabel() {
            if(this.directorInterestLocal.type === DIRECTOR_INTEREST.TYPES.TYPE_HOLDING_COMPANY)
                return "Name of company's holding company";
            if(this.directorInterestLocal.type === DIRECTOR_INTEREST.TYPES.TYPE_ULTIMATE_HOLDING_COMPANY)
                return "Name of company's ultimate holding company";
            if(this.directorInterestLocal.type === DIRECTOR_INTEREST.TYPES.TYPE_SUBSIDIARY_COMPANY)
                return "Name of company's subsidiary";
            if(this.directorInterestLocal.type === DIRECTOR_INTEREST.TYPES.TYPE_SUBSIDIARY_HOLDING_COMPANY)
                return "Name of subsidiary of company's holding company";
            return "Name of company";
        },

        getDynamicNameLabel() {
            if(this.directorInterestLocal.howHeld === DIRECTOR_INTEREST.HOW_HELD.HOW_HELD_NAME_OF_BODIES_OPTIONS)
                return "Name of bodies corporate";
            if(this.directorInterestLocal.howHeld === DIRECTOR_INTEREST.HOW_HELD.HOW_HELD_SPOUSE_NOMINEE_OPTIONS)
                return "Name of spouse/nominee's";
            return "Name";
        },

        isDisabled() {
            return this.dataStatus === "view";
        },

        shouldDisabledCompany() {
            if(this.dataStatus === "view") 
                return true;
            if(this.directorInterestLocal?.type === DIRECTOR_INTEREST.TYPES.TYPE_OWN_COMPANY)
                return true;

            return false;
        },

        shouldDisabledName() {
            if(this.dataStatus === "view") 
                return true;
            if(this.directorInterestLocal.howHeld) {
                if(this.directorInterestLocal.howHeld.toLowerCase() === DIRECTOR_INTEREST.HOW_HELD.HOW_HELD_HOLDER_OPTIONS.toLowerCase())
                    return true;
            }

            return false;
        },

        isDirectorEdit() {
            return this.dataStatus === "edit";
        },

        sideBarTitle() {
            if(this.dataStatus === "create")
                return "Add Director Interest";

            if(this.dataStatus === "view")
                return "Director Interest Details";

            return "Update Director Interest";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },

        currencyEmptyMessage() {
            if (! this.directorInterestLocal.typeOfShares)
                return "Please select Type of Shares";

            return NO_MATCHING_OPTIONS;
        },

        typeEmptyMessage() {
            if (! this.directorInterestLocal.director?.name)
                return "Please select Director";
            return NO_MATCHING_OPTIONS;
        },

        howHeldEmptyMessage() {
            if (! this.directorInterestLocal.type)
            {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.howHeldOptions = [];
                return "Please select Type";
            }

            return NO_MATCHING_OPTIONS;
        },
    },

    watch: {
        dataStatus(status) {
            if (status === 'create') {
                this.getDirectorInterestRequiredData();
            }
            if(status === 'edit') {
                this.getDirectorInterestRequiredData();
                this.handleTypeOfSharesOnChange();
            }
        },

        isDirectorInterestSidebarActive(activeStatus) {
            if(!activeStatus) {
                this.currencyOptions = [];
            }
            if(activeStatus) {
                this.handleTypeOnChange(this.dataDirectorInterest.type);
            }
        },
    },

    methods: {
        async handleTypeOfSharesOnChange()
        {
            this.isCurrencyOptionsLoading = true;
            this.directorInterestLocal.currency = null;
            
            let typeOfSharesId; 
            if(this.directorInterestLocal?.typeOfShares?.id) 
                typeOfSharesId = this.directorInterestLocal.typeOfShares.id;
            else typeOfSharesId = this.dataDirectorInterest.typeOfShares.id;

            // get currencies
            const currencies = await Company.getSupportedCurrencies(typeOfSharesId);
            this.currencyOptions = currencies;

            this.isCurrencyOptionsLoading = false;
        },

        onAcquireChange() {
            if (! this.directorInterestLocal.dateOfAgreement && ! this.directorInterestLocal.priceOrOtherConsideration) 
                this.shouldDisableCessation = false;
            else
                this.shouldDisableCessation = true;
        },

        onCessationChange() {
            if (! this.directorInterestLocal.cessationDateOfAgreement && ! this.directorInterestLocal.cessationPriceOrOtherConsideration) 
                this.shouldDisableAcquire = false;
            else 
                this.shouldDisableAcquire = true;
        },

        async getDirectorInterestRequiredData() {
            this.isFetching = true;

            // get share types
            const shareTypes = await Company.getSupportedShareTypes();
            this.shareTypeOptions = shareTypes;

            // get directorOptions
            const directors = await DirectorInterest.directors();
            this.directorOptions = directors.map(director => ({
                // id: director.id,
                userId: director.user_id,
                nric: director.user?.user_info?.NRIC,
                name: director.user?.user_info?.legal_name ?? director.user.name,
                appointment: director.appointment_date,
                cessation: director.cessation_date,
                shareholderType: director.shareholder_type,
                numberOfShares: director.number_of_shares,
            }));

            // currency option
            const currencies = await Currency.all();

            if(this.dataStatus === 'create') {
                const defaultCurrency = await Currency.getDefaultCurrency();
                this.directorInterestLocal.acquiredPriceCurrency = defaultCurrency;
                this.directorInterestLocal.cessationPriceCurrency = defaultCurrency;

                this.directorInterestLocal.cessationPriceOrOtherConsideration = 0.00;
                this.directorInterestLocal.priceOrOtherConsideration = 0.00;
            }

            this.allCurrencies = currencies;

            this.isFetching = false;
        },

        async getHowHeldOptions() {
            const directorInterestOptions = await DirectorInterest.getHowHeldOptions();

            this.typeOptionsData = Object.keys(directorInterestOptions);
            
            if(this.dataStatus === 'edit')
                this.typeOptions = this.typeOptionsData;

            this.howHeldOptionsData = directorInterestOptions;
            return this.howHeldOptionsData;
        },

        handleDirectorOnChange(value)
        {
            this.typeOptions = this.typeOptionsData;

            this.directorInterestLocal.numberOfShares = value.numberOfShares;
        },

        handleTypeOnChange(type) {
            if(type === DIRECTOR_INTEREST.TYPES.TYPE_OWN_COMPANY) 
                this.directorInterestLocal.companyName = this.companyName;
            else this.directorInterestLocal.companyName = null;

            if(this.howHeldOptionsData.length < 1) {
                this.getHowHeldOptions()
                    .then(() => {
                        this.howHeldOptions= this.howHeldOptionsData[type];
                    });
            }
            else this.howHeldOptions= this.howHeldOptionsData[type];
        },

        handleHowHeldOnChange(option) {
            if(option === DIRECTOR_INTEREST.HOW_HELD.HOW_HELD_HOLDER_OPTIONS) 
                this.directorInterestLocal.holderName = this.directorInterestLocal.director.name;
            else this.directorInterestLocal.holderName = null;
        },

        showToastTip(option) {
            const toLower = option.toLowerCase();
            this.selectedTip = this.tips.find(tip => {
                if(tip.name.toLowerCase() === toLower)
                    return tip.value;
                return null;
            });
        },

        getDynamicId(name) {
            const join = name.toLowerCase().replace(/\w+/g, txt => 
                txt.charAt(0).toUpperCase() + txt.substr(1)
            ).replace(/\s/g, '');

            return join.charAt(0).toLowerCase() + join.substr(1);
        }
    },

    setup(props, { emit }) {
        const maskKeys = ref(['numberOfShares', 'priceOrOtherConsideration', 'cessationPriceOrOtherConsideration']);

        // eslint-disable-next-line no-shadow
        function useDirectorInterestHandler(props, emit) {
            const directorInterestLocal = ref(JSON.parse(JSON.stringify(props.dataDirectorInterest.value)));
            const resetDirectorInterestLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                directorInterestLocal.value = JSON.parse(JSON.stringify(props.dataDirectorInterest.value));
            };

            watch(props.dataDirectorInterest, () => {
                resetDirectorInterestLocal();
            });
           
            const onSubmit = () => {
                const directorInterestData = JSON.parse(JSON.stringify(directorInterestLocal));
                
                if (props.dataDirectorInterest.value.id) emit('update-director-interest', unMask(directorInterestData.value, maskKeys.value));
                else emit('add-director-interest', unMask(directorInterestData.value, maskKeys.value));
            };

            return {
                directorInterestLocal, 

                // UI
                onSubmit,
                resetDirectorInterestLocal,
            };
        };
         
        const {
            directorInterestLocal,

            // UI
            onSubmit, 
            resetDirectorInterestLocal
        } = useDirectorInterestHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(directorInterestLocal);

        function clearFormData() {
            resetDirectorInterestLocal();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            directorInterestLocal,

            // Form Validation
            resetForm,
            clearForm,
            refFormObserver,
            getValidationState, 
            
            // Methods
            clearFormData
        };
    },
};
</script>

<style scope>
.director-interest-create-or-edit .preloader {
    width: 15px;
    height: 15px;
}

.director-interest-create-or-edit .preloader div:before {
    background: var(--primary-color);
}

.director-interest-create-or-edit .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}

.select-toll-tips {
    float: right;
    font-size: 15px;
}

.tooltip .tooltip-inner {
  max-width: 430px !important;
  width: 300px !important;
  font-size: calc(1.155rem - 2px);
  text-align: justify !important;
  hyphens: auto;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>